import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Switch, Tag, Tooltip, notification } from "antd";
import { DeleteOutlined, EditOutlined, ExportOutlined, PlusOutlined } from "@ant-design/icons";
import {
  createAgentKnowledge,
  deleteAgentKnowledge,
  fetchAgentKnowledge,
  setAgentKnowledgeOff,
  updateAgentKnowledge,
} from "util/agentKnowledgeApi";
import KnowledgeRecordModal from "./KnowledgeRecordModal";
import { knowledgeErrorText } from "./knowledgeErrors";
import styles from "./style.module.scss";

const topicKey = (topic) => String(topic || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");
const isGeneral = (topic) => ["", topicKey("Genel")].includes(topicKey(topic));

// A voice survey's view of the company knowledge base (Distribute → AI phone survey): which
// shared records it uses, the ones it switches off, and its own extra records.
const SurveyKnowledgePanel = ({ instrumentId, questions, locked, onLoaded }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchAgentKnowledge(instrumentId);
    if (!res || res.error) return;
    setData(res);
    if (onLoaded) onLoaded(res);
  }, [instrumentId, onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const questionTopics = useMemo(
    () => new Set((questions || []).map((q) => topicKey(q.topic)).filter(Boolean)),
    [questions]
  );

  if (!data) return null;

  const fail = (res) => notification.error({ message: knowledgeErrorText(t, res), placement: "bottomRight" });
  const editable = !!instrumentId && !locked && !data.read_only;
  const offIds = new Set(data.off_ids || []);
  const company = (data.records || []).filter((r) => !r.instrument_id);
  const own = (data.records || []).filter((r) => r.instrument_id);
  const ownGuidelineTopics = new Set(own.filter((r) => r.kind === "guideline").map((r) => topicKey(r.topic)));
  const usedCount = company.filter((r) => r.active && !offIds.has(r.id)).length;

  const toggle = async (record, on) => {
    setBusyId(record.id);
    const res = await setAgentKnowledgeOff(record.id, instrumentId, !on);
    setBusyId(null);
    if (!res || res.error) fail(res);
    else load();
  };

  const save = async (values) => {
    setSaving(true);
    const res = editing
      ? await updateAgentKnowledge(editing.id, values)
      : await createAgentKnowledge({ ...values, instrument_id: instrumentId });
    setSaving(false);
    if (!res || res.error) {
      fail(res);
      return;
    }
    setModalOpen(false);
    setEditing(null);
    load();
  };

  const remove = (record) =>
    Modal.confirm({
      title: t("surveySettings.knowledge.delete_title"),
      content: t("surveySettings.survey.delete_own_body"),
      okText: t("surveySettings.knowledge.delete_ok"),
      okButtonProps: { danger: true },
      cancelText: t("surveySettings.knowledge.cancel"),
      onOk: async () => {
        const res = await deleteAgentKnowledge(record.id);
        if (!res || res.error) fail(res);
        else load();
      },
    });

  // Why a company record will not reach this survey's agent, if it will not.
  const unusedReason = (r) => {
    if (!r.active) return t("surveySettings.survey.unused_inactive");
    if (r.kind !== "guideline" || isGeneral(r.topic)) return null;
    if (ownGuidelineTopics.has(topicKey(r.topic))) return t("surveySettings.survey.unused_replaced");
    if (!questionTopics.has(topicKey(r.topic))) return t("surveySettings.survey.unused_no_question");
    return null;
  };

  const recordText = (r) => (
    <span className={styles.surveyRowText}>
      {r.topic} · {r.kind === "answer" ? `${r.trigger} → ${r.response}` : r.response}
    </span>
  );

  const kindTag = (r) => (
    <Tag className={styles.kindTag} color={r.kind === "answer" ? "purple" : "cyan"}>
      {t(r.kind === "answer" ? "surveySettings.knowledge.kind_answer" : "surveySettings.knowledge.kind_guideline")}
    </Tag>
  );

  return (
    <div className={styles.surveyPanel}>
      <div className={styles.surveyPanelHeader}>
        <span>{t("surveySettings.survey.summary", { used: usedCount, total: company.length })}</span>
        <Button
          size="small"
          icon={<ExportOutlined />}
          onClick={() => window.open("/console/sonic-survey/settings", "_blank", "noopener")}
        >
          {t("surveySettings.survey.open_settings")}
        </Button>
      </div>
      {!instrumentId ? <p className={styles.intro}>{t("surveySettings.survey.save_first")}</p> : null}

      {company.map((r) => {
        const reason = unusedReason(r);
        return (
          <div key={r.id} className={`${styles.surveyRow} ${reason || offIds.has(r.id) ? styles.inactiveRow : ""}`}>
            {kindTag(r)}
            <span>
              {recordText(r)}
              {reason ? <span className={styles.unusedHint}>{reason}</span> : null}
            </span>
            <Tooltip title={t("surveySettings.survey.switch_hint")}>
              <Switch
                size="small"
                checked={!offIds.has(r.id)}
                disabled={!editable || !r.active}
                loading={busyId === r.id}
                onChange={(on) => toggle(r, on)}
              />
            </Tooltip>
          </div>
        );
      })}

      <p className={styles.sectionLabel}>{t("surveySettings.survey.own_title")}</p>
      {own.map((r) => (
        <div key={r.id} className={styles.surveyRow}>
          {kindTag(r)}
          {recordText(r)}
          {editable ? (
            <span>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditing(r);
                  setModalOpen(true);
                }}
              />
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r)} />
            </span>
          ) : (
            <span />
          )}
        </div>
      ))}
      {editable ? (
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          {t("surveySettings.survey.add_own")}
        </Button>
      ) : null}

      <KnowledgeRecordModal
        open={modalOpen}
        record={editing}
        topics={data.topics}
        saving={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />
    </div>
  );
};

export default SurveyKnowledgePanel;
